// YERİ: src/app/domains/website/core/site-texts.store.ts
//
// VİTRİNİN SAYFA-DIŞI METİNLERİ — 404 sayfası, blog başlığı, site geneli
// yapısal verideki (JSON-LD) tanıtım cümleleri.
//
// Bu metinler bir CMS SAYFASINA ait değil (404 ve blog, CMS sayfası olmadan
// çizilir), o yüzden bir dönem kodda İngilizce sabitti: Türkçe vitrinde de
// "Page not found" yazıyordu ve panelden değiştirilemiyordu. Artık Ana Şablon'un
// (dil başına) "Site Metinleri" bölümünden (mkt-site-texts) geliyor; web-layout
// çerçeveyi kurarken bu bölümün config'ini buraya yazar.
//
// Alan boşsa ZİYARETÇİNİN DİLİNDEKİ yedek metin kullanılır (SITE_TEXT_BY_LANG);
// o dil listede yoksa İngilizce. Bölüm hiç eklenmemiş bir dilde bile site tek
// dilde görünür.

import { Injectable, inject, signal } from '@angular/core';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { LanguageStore } from './language.store';

export const SITE_TEXT_DEFAULTS = {
  notFoundTitle: 'Page not found',
  notFoundText: 'The page you are looking for doesn’t exist or has moved.',
  notFoundCta: 'Back to home',
  blogTitle: 'Blog',
  blogSubtitle: 'Product news, playbooks and industry deep dives.',
  blogSeoDescription: 'Product news, playbooks and industry deep dives from Melbarrel.',
  blogEmpty: 'No posts yet.',
  blogBack: 'Blog',
  orgDescription:
    'Business management software for service businesses: CRM, bookings, staff, '
    + 'invoicing and a website builder in one system.',
  appDescription:
    'Melbarrel combines customer records, scheduling, staff management, quoting, '
    + 'invoicing, online payments and a website builder into one platform, with 30 '
    + 'industry-specific modules.',
} as const;

export type SiteTextKey = keyof typeof SITE_TEXT_DEFAULTS;

/**
 * DİL BAŞINA YEDEK — bölüm hiç eklenmemiş ya da o dilde boş bırakılmışsa.
 *
 * Eskiden tek yedek vardı ve İngilizceydi: Türkçe vitrinde olmayan bir adrese
 * giden ziyaretçi, Türkçe bir sayfanın ortasında "Page not found / Back to
 * home" görüyordu. Kiracı panelden doldurunca düzeliyordu ama doldurana kadar
 * site iki dilli görünüyordu; yedeğin de ziyaretçinin dilinde olması gerekir.
 *
 * Buradaki metinler İÇERİK DEĞİL, yedek: panelden girilen değer her zaman
 * önce gelir. Listede olmayan bir dil İngilizceye düşer (vitrinin yayın dili).
 */
const SITE_TEXT_BY_LANG: Record<string, Partial<Record<SiteTextKey, string>>> = {
  tr: {
    notFoundTitle: 'Sayfa bulunamadı',
    notFoundText: 'Aradığınız sayfa yok ya da taşınmış.',
    notFoundCta: 'Ana sayfaya dön',
    blogTitle: 'Blog',
    blogSubtitle: 'Ürün haberleri, uygulama rehberleri ve sektör yazıları.',
    blogSeoDescription: "Melbarrel'dan ürün haberleri, uygulama rehberleri ve sektör yazıları.",
    blogEmpty: 'Henüz yazı yok.',
    blogBack: 'Blog',
    orgDescription:
      'Hizmet işletmeleri için işletme yönetim yazılımı: CRM, randevu, personel, '
      + 'faturalama ve web sitesi kurucusu tek sistemde.',
    appDescription:
      'Melbarrel; müşteri kayıtlarını, randevuyu, personel yönetimini, teklif ve '
      + 'faturalamayı, online ödemeyi ve web sitesi kurucusunu 30 sektöre özel '
      + 'modülle tek platformda birleştirir.',
  },
  de: {
    notFoundTitle: 'Seite nicht gefunden',
    notFoundText: 'Die gesuchte Seite existiert nicht oder wurde verschoben.',
    notFoundCta: 'Zur Startseite',
    blogTitle: 'Blog',
    blogSubtitle: 'Produktneuheiten, Praxisleitfäden und Brancheneinblicke.',
    blogSeoDescription: 'Produktneuheiten, Praxisleitfäden und Brancheneinblicke von Melbarrel.',
    blogEmpty: 'Noch keine Beiträge.',
    blogBack: 'Blog',
    orgDescription:
      'Unternehmenssoftware für Dienstleister: CRM, Terminbuchung, Personal, '
      + 'Rechnungsstellung und Website-Baukasten in einem System.',
    appDescription:
      'Melbarrel vereint Kundendaten, Terminplanung, Personalverwaltung, Angebote, '
      + 'Rechnungen, Online-Zahlungen und einen Website-Baukasten in einer Plattform '
      + 'mit 30 branchenspezifischen Modulen.',
  },
  fr: {
    notFoundTitle: 'Page introuvable',
    notFoundText: "La page que vous cherchez n'existe pas ou a été déplacée.",
    notFoundCta: "Retour à l'accueil",
    blogTitle: 'Blog',
    blogSubtitle: 'Actualités produit, guides pratiques et analyses sectorielles.',
    blogSeoDescription: 'Actualités produit, guides pratiques et analyses sectorielles de Melbarrel.',
    blogEmpty: 'Aucun article pour le moment.',
    blogBack: 'Blog',
    orgDescription:
      'Logiciel de gestion pour les entreprises de services : CRM, réservations, '
      + 'personnel, facturation et créateur de site web dans un seul système.',
    appDescription:
      'Melbarrel réunit fichiers clients, planning, gestion du personnel, devis, '
      + 'facturation, paiements en ligne et créateur de site web sur une seule '
      + 'plateforme, avec 30 modules sectoriels.',
  },
  it: {
    notFoundTitle: 'Pagina non trovata',
    notFoundText: 'La pagina che cerchi non esiste o è stata spostata.',
    notFoundCta: 'Torna alla home',
    blogTitle: 'Blog',
    blogSubtitle: 'Novità di prodotto, guide pratiche e approfondimenti di settore.',
    blogSeoDescription: 'Novità di prodotto, guide pratiche e approfondimenti di settore da Melbarrel.',
    blogEmpty: 'Nessun articolo per ora.',
    blogBack: 'Blog',
    orgDescription:
      'Software gestionale per le imprese di servizi: CRM, prenotazioni, personale, '
      + 'fatturazione e costruttore di siti web in un unico sistema.',
    appDescription:
      'Melbarrel unisce anagrafiche clienti, agenda, gestione del personale, preventivi, '
      + 'fatturazione, pagamenti online e costruttore di siti web in una sola piattaforma, '
      + 'con 30 moduli settoriali.',
  },
  es: {
    notFoundTitle: 'Página no encontrada',
    notFoundText: 'La página que busca no existe o se ha movido.',
    notFoundCta: 'Volver al inicio',
    blogTitle: 'Blog',
    blogSubtitle: 'Novedades del producto, guías prácticas y análisis del sector.',
    blogSeoDescription: 'Novedades del producto, guías prácticas y análisis del sector de Melbarrel.',
    blogEmpty: 'Todavía no hay entradas.',
    blogBack: 'Blog',
    orgDescription:
      'Software de gestión para empresas de servicios: CRM, reservas, personal, '
      + 'facturación y creador de sitios web en un único sistema.',
    appDescription:
      'Melbarrel reúne fichas de clientes, agenda, gestión de personal, presupuestos, '
      + 'facturación, pagos en línea y creador de sitios web en una sola plataforma, '
      + 'con 30 módulos sectoriales.',
  },
  pt: {
    notFoundTitle: 'Página não encontrada',
    notFoundText: 'A página que procura não existe ou foi movida.',
    notFoundCta: 'Voltar ao início',
    blogTitle: 'Blog',
    blogSubtitle: 'Novidades do produto, guias práticos e análises do setor.',
    blogSeoDescription: 'Novidades do produto, guias práticos e análises do setor da Melbarrel.',
    blogEmpty: 'Ainda não há artigos.',
    blogBack: 'Blog',
    orgDescription:
      'Software de gestão para empresas de serviços: CRM, marcações, pessoal, '
      + 'faturação e construtor de sites num único sistema.',
    appDescription:
      'A Melbarrel reúne fichas de clientes, agenda, gestão de pessoal, orçamentos, '
      + 'faturação, pagamentos online e construtor de sites numa só plataforma, '
      + 'com 30 módulos setoriais.',
  },
  ru: {
    notFoundTitle: 'Страница не найдена',
    notFoundText: 'Искомой страницы не существует или она перемещена.',
    notFoundCta: 'На главную',
    blogTitle: 'Блог',
    blogSubtitle: 'Новости продукта, практические руководства и отраслевые разборы.',
    blogSeoDescription: 'Новости продукта, практические руководства и отраслевые разборы от Melbarrel.',
    blogEmpty: 'Записей пока нет.',
    blogBack: 'Блог',
    orgDescription:
      'Программа управления бизнесом для сферы услуг: CRM, запись, персонал, '
      + 'выставление счетов и конструктор сайта в одной системе.',
    appDescription:
      'Melbarrel объединяет клиентскую базу, расписание, управление персоналом, '
      + 'коммерческие предложения, счета, онлайн-оплату и конструктор сайта в одной '
      + 'платформе с 30 отраслевыми модулями.',
  },
  ar: {
    notFoundTitle: 'لم يُعثر على الصفحة',
    notFoundText: 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها.',
    notFoundCta: 'العودة إلى الصفحة الرئيسية',
    blogTitle: 'المدوّنة',
    blogSubtitle: 'أخبار المنتج وأدلة عملية وتحليلات قطاعية.',
    blogSeoDescription: 'أخبار المنتج وأدلة عملية وتحليلات قطاعية من Melbarrel.',
    blogEmpty: 'لا توجد مقالات بعد.',
    blogBack: 'المدوّنة',
    orgDescription:
      'برنامج إدارة أعمال لشركات الخدمات: إدارة العملاء والحجوزات والموظفين '
      + 'والفوترة ومنشئ موقع إلكتروني في نظام واحد.',
    appDescription:
      'يجمع Melbarrel سجلات العملاء والجدولة وإدارة الموظفين وعروض الأسعار والفوترة '
      + 'والدفع الإلكتروني ومنشئ المواقع في منصة واحدة، مع 30 وحدة مخصّصة للقطاعات.',
  },
  fa: {
    notFoundTitle: 'صفحه یافت نشد',
    notFoundText: 'صفحه‌ای که دنبال آن هستید وجود ندارد یا جابه‌جا شده است.',
    notFoundCta: 'بازگشت به صفحهٔ اصلی',
    blogTitle: 'وبلاگ',
    blogSubtitle: 'اخبار محصول، راهنماهای کاربردی و تحلیل‌های صنعتی.',
    blogSeoDescription: 'اخبار محصول، راهنماهای کاربردی و تحلیل‌های صنعتی از Melbarrel.',
    blogEmpty: 'هنوز مطلبی منتشر نشده است.',
    blogBack: 'وبلاگ',
    orgDescription:
      'نرم‌افزار مدیریت کسب‌وکار برای شرکت‌های خدماتی: مدیریت مشتری، نوبت‌دهی، '
      + 'کارکنان، صدور فاکتور و سایت‌ساز در یک سامانه.',
    appDescription:
      'Melbarrel پروندهٔ مشتریان، زمان‌بندی، مدیریت کارکنان، پیش‌فاکتور، صدور فاکتور، '
      + 'پرداخت آنلاین و سایت‌ساز را با ۳۰ ماژول تخصصی صنعتی در یک پلتفرم گرد می‌آورد.',
  },
};

/** Ana Şablon'daki bölüm tipi. */
export const SITE_TEXTS_BLOCK = 'mkt-site-texts';

@Injectable({ providedIn: 'root' })
export class MktSiteTexts {
  private lang = inject(LanguageStore);

  /** Aktif dilin "Site Metinleri" bölümünün config'i (yoksa boş). */
  readonly config = signal<SiteConfig>({});

  text(key: SiteTextKey): string {
    // 'en-ca' gibi bölgeli kodlar ana dile düşsün.
    const kod = (this.lang.current() || 'en').toLowerCase().split('-')[0];
    const yedek = SITE_TEXT_BY_LANG[kod]?.[key] ?? (SITE_TEXT_DEFAULTS[key] as string);
    return cfg(this.config(), key, yedek);
  }
}
